import React, { useState, useEffect, useCallback } from 'react';
import { evaluationPilotAPI, usersAPI } from '../../services/api';
import RequiredFieldLabel from '../Common/RequiredFieldLabel';
import { isPilotFieldRequired, validateEvaluationPilot, hasValidationErrors } from '../../utils/validation';

function EvaluationPilot() {
  const currentUserId = localStorage.getItem('id');
  const userRole = localStorage.getItem('role');
  const [pilots, setPilots] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 12
  });
  const [filters, setFilters] = useState({
    search: '',
    pilotStatus: 'all',
    likelihood: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [selectedPilot, setSelectedPilot] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pilotData, setPilotData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canEdit, setCanEdit] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getPublicUsers();
      console.log('Users loaded:', response.data?.length || 0);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchPilots = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const response = await evaluationPilotAPI.getAll(params);
      setPilots(response.data.evaluationPilots || []);
      if (response.data.pagination) {
        setPagination(prev => ({ ...prev, ...response.data.pagination }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch pilot records');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.limit]);

  useEffect(() => {
    fetchPilots();
    fetchUsers();
  }, [fetchPilots]);

  const openModal = (pilot) => {
    setSelectedPilot(pilot);
    
    // Check if user can edit this pilot record
    const isAssigned = pilot.lead?.primaryAssignee?._id === currentUserId ||
                      pilot.lead?.secondaryAssignees?.some(assignee => assignee._id === currentUserId);
    setCanEdit(userRole === 'admin' || isAssigned);
    
    setPilotData({
      pilotStartDate: pilot.pilotStartDate ? new Date(pilot.pilotStartDate).toISOString().split('T')[0] : '',
      pilotEndDate: pilot.pilotEndDate ? new Date(pilot.pilotEndDate).toISOString().split('T')[0] : '',
      pilotDuration: pilot.pilotDuration || '',
      pilotScope: pilot.pilotScope || '',
      pilotUsers: pilot.pilotUsers || '',
      pilotFeedback: pilot.pilotFeedback || '',
      technicalRequirements: pilot.technicalRequirements || '',
      integrationNeeds: pilot.integrationNeeds || '',
      budgetDiscussed: pilot.budgetDiscussed || '',
      decisionMakers: pilot.decisionMakers?.join(', ') || '',
      competitorAnalysis: pilot.competitorAnalysis || '',
      riskFactors: pilot.riskFactors || '',
      successCriteria: pilot.successCriteria || '',
      nextMilestone: pilot.nextMilestone || '',
      nextMilestoneDate: pilot.nextMilestoneDate ? new Date(pilot.nextMilestoneDate).toISOString().split('T')[0] : '',
      pilotStatus: pilot.pilotStatus || 'Planning',
      evaluationScore: pilot.evaluationScore || '',
      likelihood: pilot.likelihood || 'Medium',
      notes: pilot.notes || ''
    });
    setShowModal(true);
  };

  const savePilotData = async () => {
    const errors = validateEvaluationPilot(pilotData);
    if (hasValidationErrors(errors)) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const dataToSave = {
        ...pilotData,
        decisionMakers: pilotData.decisionMakers.split(',').map(name => name.trim()).filter(name => name)
      };
      
      await evaluationPilotAPI.update(selectedPilot._id, dataToSave);
      setSuccess('Pilot information updated successfully!');
      setShowModal(false);
      await fetchPilots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update pilot information');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-5 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-indigo-600 bg-clip-text text-transparent">
              Evaluation & Pilot Management
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage leads in evaluation and pilot phase</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm sm:text-base">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 text-sm sm:text-base">
            {success}
          </div>
        )}

        {/* Filters */}
        <div className="bg-gray-50 rounded-xl p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
            <input
              type="text"
              placeholder="Search by school name..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
            />
            
            <select
              value={filters.pilotStatus}
              onChange={(e) => handleFilterChange('pilotStatus', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
            >
              <option value="all">All Pilot Status</option>
              <option value="Planning">Planning</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Extended">Extended</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            
            <select
              value={filters.likelihood}
              onChange={(e) => handleFilterChange('likelihood', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
            >
              <option value="all">All Likelihood</option>
              <option value="Very High">Very High</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
              <option value="Very Low">Very Low</option>
            </select>
          </div>
          
          <div className="flex justify-between items-center">
            <div className="text-xs sm:text-sm text-gray-600">
              Showing {pilots.length} of {pagination.totalCount} pilot records
            </div>
          </div>
        </div>

        {loading && <p className="text-center py-8 text-sm sm:text-base">Loading pilot records...</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {pilots.map((pilot) => (
            <div key={pilot._id} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex justify-between items-start mb-4 gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{pilot.lead?.schoolName}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{pilot.lead?.email}</p>
                </div>
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                  pilot.pilotStatus === 'Completed' ? 'bg-green-100 text-green-800' :
                  pilot.pilotStatus === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                  pilot.pilotStatus === 'Cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {pilot.pilotStatus}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Likelihood:</span> 
                  <span className={`ml-1 px-2 py-1 rounded text-xs ${
                    pilot.likelihood === 'Very High' ? 'bg-green-100 text-green-800' :
                    pilot.likelihood === 'High' ? 'bg-blue-100 text-blue-800' :
                    pilot.likelihood === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {pilot.likelihood}
                  </span>
                </p>
                {pilot.evaluationScore && (
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-medium">Score:</span> {pilot.evaluationScore}/10
                  </p>
                )}
                {pilot.pilotDuration && (
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-medium">Duration:</span> {pilot.pilotDuration} days
                  </p>
                )}
                {pilot.nextMilestone && (
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    <span className="font-medium">Next Milestone:</span> {pilot.nextMilestone}
                  </p>
                )}
              </div>

              <button
                onClick={() => openModal(pilot)}
                className={`w-full px-3 sm:px-4 py-2 rounded-lg transition text-xs sm:text-sm font-medium ${
                  userRole === 'admin' || pilot.lead?.primaryAssignee?._id === currentUserId || 
                  pilot.lead?.secondaryAssignees?.some(assignee => assignee._id === currentUserId)
                    ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                {userRole === 'admin' || pilot.lead?.primaryAssignee?._id === currentUserId || 
                 pilot.lead?.secondaryAssignees?.some(assignee => assignee._id === currentUserId)
                  ? 'Manage Pilot Info'
                  : 'View Pilot Info'
                }
              </button>
            </div>
          ))}
        </div>

        {!loading && pilots.length === 0 && (
          <div className="text-center py-12">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Pilot Records</h3>
            <p className="text-sm sm:text-base text-gray-500">No leads in evaluation/pilot phase found.</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="flex justify-center items-center flex-wrap gap-2 mt-6">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="px-2 sm:px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const pageNum = Math.max(1, pagination.currentPage - 2) + i;
              if (pageNum > pagination.totalPages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-2 sm:px-3 py-2 rounded-lg border text-xs sm:text-sm ${
                    pageNum === pagination.currentPage
                      ? 'bg-indigo-500 text-white border-indigo-500'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="px-2 sm:px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
            >
              Next
            </button>
          </div>
        )}

        {/* Modal */}
        {showModal && selectedPilot && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-start mb-4 sm:mb-6 gap-2">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 pr-2">Pilot Information - {selectedPilot.lead?.schoolName}</h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <RequiredFieldLabel required={isPilotFieldRequired('pilotStartDate')}>Pilot Start Date</RequiredFieldLabel>
                    <input
                      type="date"
                      value={pilotData.pilotStartDate}
                      onChange={(e) => canEdit && setPilotData({...pilotData, pilotStartDate: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      readOnly={!canEdit}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Pilot End Date</label>
                    <input
                      type="date"
                      value={pilotData.pilotEndDate}
                      onChange={(e) => canEdit && setPilotData({...pilotData, pilotEndDate: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      readOnly={!canEdit}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Duration (Days)</label>
                    <input
                      type="number"
                      value={pilotData.pilotDuration}
                      onChange={(e) => setPilotData({...pilotData, pilotDuration: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Number of Users</label>
                    <input
                      type="number"
                      value={pilotData.pilotUsers}
                      onChange={(e) => setPilotData({...pilotData, pilotUsers: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required={isPilotFieldRequired('pilotStatus')}>Pilot Status</RequiredFieldLabel>
                    <select
                      value={pilotData.pilotStatus}
                      onChange={(e) => canEdit && setPilotData({...pilotData, pilotStatus: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={!canEdit}
                    >
                      <option value="Planning">Planning</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Extended">Extended</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Likelihood</label>
                    <select
                      value={pilotData.likelihood}
                      onChange={(e) => setPilotData({...pilotData, likelihood: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
                    >
                      <option value="Very High">Very High</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                      <option value="Very Low">Very Low</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Pilot Scope</label>
                    <textarea
                      value={pilotData.pilotScope}
                      onChange={(e) => canEdit && setPilotData({...pilotData, pilotScope: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 h-20 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder={canEdit ? "What features/modules are being piloted" : ""}
                      readOnly={!canEdit}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Decision Makers (comma separated)</label>
                    <input
                      type="text"
                      value={pilotData.decisionMakers}
                      onChange={(e) => setPilotData({...pilotData, decisionMakers: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
                      placeholder="John Doe, Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Success Criteria</label>
                    <textarea
                      value={pilotData.successCriteria}
                      onChange={(e) => setPilotData({...pilotData, successCriteria: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 h-20 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={pilotData.notes}
                      onChange={(e) => setPilotData({...pilotData, notes: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 h-20 text-sm sm:text-base"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  {canEdit ? (
                    <>
                      <button
                        onClick={savePilotData}
                        disabled={loading}
                        className="flex-1 bg-indigo-500 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-600 transition disabled:opacity-50 font-medium text-sm sm:text-base"
                      >
                        {loading ? 'Saving...' : 'Save Pilot Info'}
                      </button>
                      <button
                        onClick={() => setShowModal(false)}
                        className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2.5 rounded-lg text-center font-medium text-xs sm:text-sm">
                        Read-Only: You can only view this information
                      </div>
                      <button
                        onClick={() => setShowModal(false)}
                        className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium text-sm sm:text-base"
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EvaluationPilot;